/** Sub-pixel rounding buffer for baked image heights. */
export const PARALLAX_BG_EPSILON = 2;

/** Reference viewport heights when V is unknown at save (fixed mode). */
export const PARALLAX_BG_V_REF = {
  desktop: 1200,
  tablet: 1200,
  mobile: 900,
};

export const PARALLAX_INTENSITY_DEFAULT = 0.3;
export const PARALLAX_INTENSITY_MAX = 0.5;

const SIZE_TO_BP = {
  desktop: 'desktop',
  tablet: 'tablet',
  mobile: 'mobile',
};

/**
 * Normalize parallax intensity from panel (0–0.5) or legacy Rellax values.
 *
 * @param {number|string|undefined} raw
 * @returns {number}
 */
export function clampParallaxIntensity(raw) {
  const n = parseFloat(raw);
  if (Number.isNaN(n)) {
    return PARALLAX_INTENSITY_DEFAULT;
  }
  if (n >= 0 && n <= PARALLAX_INTENSITY_MAX) {
    return n;
  }
  // Legacy Rellax-style negative values approximated as moderate intensity
  if (n < 0) {
    return PARALLAX_INTENSITY_DEFAULT;
  }
  return PARALLAX_INTENSITY_MAX;
}

/**
 * Background element height B = H * (1 + s).
 *
 * @param {number} sectionHeight H in px
 * @param {number} intensity s
 * @returns {number}
 */
export function parallaxBgElementHeight(sectionHeight, intensity) {
  const H = Math.max(0, sectionHeight);
  const s = clampParallaxIntensity(intensity);
  return H * (1 + s);
}

/**
 * Required image file height baked at save time.
 *
 * @param {number} sectionHeight H in px
 * @param {number} intensity s (parallax) — ignored for fixed
 * @param {'fixed'|'parallax'} mode
 * @param {'desktop'|'tablet'|'mobile'} sizeKey
 * @returns {number}
 */
export function requiredParallaxBackgroundImageHeight(sectionHeight, intensity, mode, sizeKey = 'desktop') {
  const H = Math.max(0, sectionHeight);
  const bp = SIZE_TO_BP[sizeKey] || 'desktop';
  const vRef = PARALLAX_BG_V_REF[bp] ?? PARALLAX_BG_V_REF.desktop;

  if (mode === 'fixed') {
    return Math.ceil(Math.max(H, vRef));
  }

  const s = clampParallaxIntensity(intensity);
  return Math.ceil(H * (1 + s)) + PARALLAX_BG_EPSILON;
}

/**
 * Scroll progress p in [0, 1] while section transits the viewport.
 *
 * @param {DOMRect} rect
 * @param {number} scrollY
 * @param {number} viewportHeight
 * @returns {number}
 */
export function parallaxScrollProgress(rect, scrollY, viewportHeight) {
  const H = rect.height;
  const V = viewportHeight;
  const naturalBottom = rect.bottom + scrollY;
  const triggerScroll = Math.max(0, naturalBottom - V);
  const maxDelta = H + V;
  const delta = Math.min(Math.max(0, scrollY - triggerScroll), maxDelta);
  return maxDelta > 0 ? delta / maxDelta : 0;
}

/**
 * Centered translate for background parallax.
 *
 * @param {number} progress p in [0, 1]
 * @param {number} intensity s
 * @param {number} sectionHeight H
 * @returns {number}
 */
export function parallaxBackgroundTranslateY(progress, intensity, sectionHeight) {
  const s = clampParallaxIntensity(intensity);
  const H = Math.max(0, sectionHeight);
  return (progress - 0.5) * s * H;
}

/**
 * Read fixed/parallax background settings from backgroundAttrs (base-first).
 *
 * @param {Object} backgroundAttrs
 * @param {Object} fallbacks
 * @returns {{ useFixedBackground: boolean, mode: string, intensity: number, breakpoints: Object }}
 */
export function readFixedBackgroundSettings(backgroundAttrs = {}, fallbacks = {}) {
  const base = backgroundAttrs?.base || {};
  const useFixedBackground = base.useFixedBackground
    ?? backgroundAttrs?.useFixedBackground
    ?? fallbacks.useFixedBackground
    ?? false;
  const mode = base.fixedBackgroundMode
    ?? backgroundAttrs?.fixedBackgroundMode
    ?? fallbacks.fixedBackgroundMode
    ?? 'fixed';
  const rawSpeed = base.fixedBackgroundSpeed
    ?? backgroundAttrs?.fixedBackgroundSpeed
    ?? fallbacks.fixedBackgroundSpeed;
  const breakpoints = base.fixedBackgroundBreakpoints
    ?? backgroundAttrs?.fixedBackgroundBreakpoints
    ?? fallbacks.fixedBackgroundBreakpoints
    ?? { '1920': true, '1024': true, '576': false };

  let normalizedBreakpoints = breakpoints;
  if (typeof breakpoints === 'boolean') {
    normalizedBreakpoints = { '1920': breakpoints, '1024': breakpoints, '576': false };
  }

  return {
    useFixedBackground: !!useFixedBackground,
    mode: mode === 'parallax' ? 'parallax' : 'fixed',
    intensity: clampParallaxIntensity(rawSpeed),
    breakpoints: normalizedBreakpoints,
  };
}
